/**
 * Every *other* machine in the sky: the opposing aeroplanes, the balloons and
 * airships, and the raid targets on the ground.
 *
 * Split out of `combat-visuals.ts` — which owns the bullets and the group they
 * all live in — for the reason `scene-boot.ts` was split out of
 * `flight-scene.ts`: the host reached aislop's 400-line ceiling, and this is the
 * largest block in it that is cohesive on its own terms. Nothing here touches a
 * round, and nothing in the bullet pool touches a slot.
 *
 * Two levels of pooling, and both matter in a twelve-ship fight:
 *
 * - **by model.** A mission puts four different airframes in the sky, and each
 *   is fetched and turned into geometry exactly once however many aeroplanes of
 *   that type are flying (`meshCache`). Every one of them draws a `clone()`,
 *   which shares the geometry and the material and owns only its transform.
 * - **by participant.** A `Slot` is one `Group` per live id, so an aeroplane
 *   appearing or being destroyed costs a visibility flag rather than a reload.
 *
 * The one thing that invalidates the first pool is **ENHANCED MODELS**
 * (`docs/engineering/enhanced-models.md`): the cache is keyed by model id alone,
 * so flipping the switch has to empty it and rebuild every slot's contents from
 * the other source. `swapFleetModels` is that, and the ordering in it is the
 * whole trick.
 */

import * as THREE from "three/webgpu";
import { disposeTree } from "../three/dispose-tree.ts";
import { setModelledPropellerVisible } from "../three/enhanced-nodes.ts";
import { loadAircraftMesh } from "./aircraft-mesh.ts";
import { modelledPropellerVisible } from "./modelled-propeller.ts";
import { spinModelledPropeller } from "./propeller-blade-spin.ts";
import type { AircraftView, TargetView } from "./encounter-views.ts";

/** One drawn participant: a group holding its model, positioned every frame. */
export interface Slot {
  readonly group: THREE.Group;
  modelId: string | null;
}

/** What a fleet needs of its host's state. `combat-visuals.ts` owns the object; this owns the rules. */
export interface FleetState {
  readonly fleet: THREE.Group;
  readonly slots: Map<string, Slot>;
  /** `modelId` → the loaded aeroplane. Keyed by id alone, so flipping ENHANCED MODELS empties it. */
  readonly meshCache: Map<string, Promise<THREE.Object3D | undefined>>;
  /** Whether this fleet draws enhanced models where they exist (`flight/effects-settings.ts`). */
  enhancedModels: boolean;
  /**
   * Whether AD-012's disc layer is running, which decides whether each
   * aeroplane's *modelled* propeller is drawn (`modelled-propeller.ts`).
   *
   * Unlike ENHANCED MODELS this does **not** invalidate `meshCache`: it is a
   * visibility flag, and it is applied to each slot's own clone rather than to
   * the cached master, because `clone()` copies visibility at the moment it is
   * taken and would not hear a later flip.
   */
  propellerEffect: boolean;
  /** Bumped on every ENHANCED MODELS flip, so an in-flight load knows its fleet has been rebuilt under it. */
  fleetGeneration: number;
  disposed: boolean;
}

/** One fetch-and-build per model id, however many aeroplanes want it. */
function cachedMesh(state: FleetState, modelId: string): Promise<THREE.Object3D | undefined> {
  const existing = state.meshCache.get(modelId);
  if (existing) return existing;
  // `lodLadder` is asked for **here and nowhere else**: this is the one place a
  // model's distance from the camera is the sim's to decide rather than a
  // screen's (`three/enhanced-lod.ts`). The player's own aeroplane is loaded by
  // `scene-boot.ts` and always draws the finest rung.
  const pending = loadAircraftMesh(modelId, { enhanced: state.enhancedModels, lodLadder: true }).catch(() => undefined);
  state.meshCache.set(modelId, pending);
  return pending;
}

/**
 * Loads this slot's model and puts a copy of it in the slot's group.
 *
 * `generation` is the fleet-wide ENHANCED MODELS counter as it stood when the
 * load began. A flip while a fetch is in flight bumps it, and the arriving
 * aeroplane is dropped rather than added to a group that has since been emptied
 * and reloaded — the same last-request-wins guard the career turntable uses.
 */
function fillSlot(state: FleetState, slot: Slot, modelId: string): void {
  const generation = state.fleetGeneration;
  void cachedMesh(state, modelId).then((mesh) => {
    if (!mesh || state.disposed || generation !== state.fleetGeneration) return;
    const copy = mesh.clone();
    // On the clone, not on the cached master: the master is shared by every
    // aeroplane of this type and a clone taken later would inherit whatever the
    // flag happened to be. Every clone is told the fleet's current answer.
    setModelledPropellerVisible(copy, modelledPropellerVisible(modelId, state.propellerEffect));
    slot.group.add(copy);
  });
}

/** The group for `id`, created on first sight, with its model loaded once. */
function slotFor(state: FleetState, id: string, modelId: string | null): Slot {
  const existing = state.slots.get(id);
  if (existing) return existing;
  const slot: Slot = { group: new THREE.Group(), modelId: null };
  state.fleet.add(slot.group);
  state.slots.set(id, slot);
  if (modelId) {
    slot.modelId = modelId;
    fillSlot(state, slot, modelId);
  }
  return slot;
}

/**
 * ENHANCED MODELS, flipped mid-flight: every machine in the sky is rebuilt from
 * the other source.
 *
 * Slots keep their identity and their pose; only what hangs under them changes,
 * so nothing the caller placed this frame moves. The disposal is deliberately
 * *last*: a cached mesh is the one every clone of its type shares geometry and
 * material with, so freeing it before the clones are detached would pull the
 * buffers out from under an aeroplane still on screen.
 */
export function swapFleetModels(state: FleetState, enhanced: boolean): void {
  if (state.enhancedModels === enhanced || state.disposed) return;
  state.enhancedModels = enhanced;
  state.fleetGeneration += 1;
  const stale = [...state.meshCache.values()];
  state.meshCache.clear();
  for (const slot of state.slots.values()) {
    slot.group.clear();
    if (slot.modelId) fillSlot(state, slot, slot.modelId);
  }
  for (const pending of stale) void pending.then((mesh) => mesh && disposeTree(mesh));
}

/**
 * PROPELLER EFFECT, flipped mid-flight: every aeroplane in the sky shows or
 * hides the propeller its enhanced `.glb` was built with.
 *
 * Nothing is reloaded and nothing is freed — the disc layer and the modelled
 * blade are two representations of the same propeller, and this is the flag that
 * keeps exactly one of them on screen (`modelled-propeller.ts`). A baked `.TBL`
 * aeroplane has no such node and is untouched, as is an F.E.2b, a Gotha or a
 * Handley Page, whose blades the effect never covers.
 */
export function setFleetPropellerEffect(state: FleetState, enabled: boolean): void {
  if (state.propellerEffect === enabled || state.disposed) return;
  state.propellerEffect = enabled;
  for (const slot of state.slots.values()) {
    if (!slot.modelId) continue;
    setModelledPropellerVisible(slot.group, modelledPropellerVisible(slot.modelId, enabled));
  }
}

/**
 * Every other aeroplane in the sky, placed at its interpolated pose — and its
 * modelled propeller turned.
 *
 * `dtS` is here for the propeller and nothing else. The rate is the one already
 * on the view (`encounter-views.ts` derives `propellerRpm` beside the pose, off
 * the same `SimState` AD-012's disc reads), so a Gotha's nacelles and a Camel's
 * nose turn at one rate and a machine whose engine has stopped and which is not
 * windmilling stands still (`propeller-blade-spin.ts`).
 */
export function placeAircraft(state: FleetState, views: readonly AircraftView[], dtS: number): void {
  for (const view of views) {
    const slot = slotFor(state, view.id, view.modelId);
    slot.group.visible = true;
    slot.group.position.set(view.pose.position.x, view.pose.position.y, view.pose.position.z);
    slot.group.quaternion.set(
      view.pose.orientation.x,
      view.pose.orientation.y,
      view.pose.orientation.z,
      view.pose.orientation.w,
    );
    // On the slot's group, not the clone inside it: the group outlives an
    // ENHANCED MODELS flip and the phase rides on it, so an aeroplane swapped
    // mid-flight keeps the blade angle it had rather than snapping to zero.
    spinModelledPropeller(slot.group, view.propellerRpm ?? 0, dtS);
  }
}

export function placeTargets(state: FleetState, views: readonly TargetView[]): void {
  for (const view of views) {
    const slot = slotFor(state, view.id, view.modelId);
    slot.group.visible = !view.destroyed;
    slot.group.position.set(view.position.x, view.position.y, view.position.z);
    // Yaw only: neither a tethered balloon nor a Zeppelin banks.
    slot.group.rotation.set(0, -view.headingRad, 0);
  }
}

/** Anything the sim no longer reports is hidden rather than freed — it may come back next frame. */
export function hideMissing(state: FleetState, live: ReadonlySet<string>): void {
  for (const [id, slot] of state.slots) {
    if (!live.has(id)) slot.group.visible = false;
  }
}
